import { useState } from 'react'
import { getBookingDraft, saveBookingDraft } from '../../data/ceramic-booking'
import { formatCurrencyFromCents } from '../../data/protection-catalog'
import { callSupabaseFunction } from '../../lib/api'
import { CERAMIC_BOOKING_DEPOSIT_CENTS } from '../../lib/booking-domain'
import type { DepositStatus } from '../../lib/deposit-checkout'
import { Button } from '../ui/button'

export type DepositUiState =
  | 'idle'
  | 'starting'
  | 'verifying'
  | 'paid'
  | 'cancelled'
  | 'error'
  | 'unconfigured'

export interface BookDepositStepProps {
  packageKey: string | null
  addOnIds: string[]
  noAdditionalProtection: boolean
  depositStatus: DepositStatus
  uiState: DepositUiState
  stripeEnabled: boolean
  stripeMode: 'test' | 'live' | 'unconfigured' | 'unknown'
  message: string | null
  onStatus: (status: DepositStatus) => void
  onUiState: (state: DepositUiState) => void
  onMessage: (message: string | null) => void
  onReservationExpired?: () => void
}

export function BookDepositStep({
  packageKey,
  addOnIds,
  noAdditionalProtection,
  depositStatus,
  uiState,
  stripeEnabled,
  stripeMode,
  message,
  onStatus,
  onUiState,
  onMessage,
  onReservationExpired,
}: Readonly<BookDepositStepProps>) {
  const [busy, setBusy] = useState(false)
  const paid = depositStatus === 'paid' || uiState === 'paid'

  async function startCheckout() {
    if (!packageKey || busy || paid) return
    setBusy(true)
    onUiState('starting')
    onMessage(null)
    try {
      const draft = getBookingDraft()
      const response = await callSupabaseFunction('bookings-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          packageKey,
          addOns: addOnIds,
          noAdditionalProtection,
          publicReference: draft.publicReference,
        }),
      })
      const payload = await response.json() as {
        ok?: boolean
        alreadyPaid?: boolean
        checkoutUrl?: string
        publicReference?: string
        checkoutSessionId?: string
        depositStatus?: DepositStatus
        error?: string
        code?: string
        reason?: string
        nextStep?: string
      }

      if (payload.reason === 'reservation_expired' || payload.nextStep === 'appointment') {
        onUiState('error')
        onMessage(payload.error ?? 'Your appointment hold has expired. Please choose a new appointment time before paying.')
        onReservationExpired?.()
        return
      }

      if (payload.publicReference) {
        saveBookingDraft({
          ...getBookingDraft(),
          publicReference: payload.publicReference,
          checkoutSessionId: payload.checkoutSessionId,
        })
      }

      if (payload.alreadyPaid || payload.depositStatus === 'paid') {
        onStatus('paid')
        onUiState('paid')
        return
      }

      if (payload.code === 'stripe_not_configured' || !stripeEnabled) {
        onUiState('unconfigured')
        onMessage(payload.error ?? 'Stripe deposit checkout is not configured yet.')
        return
      }

      if (!response.ok || !payload.checkoutUrl) {
        throw new Error(payload.error || 'Unable to start checkout.')
      }

      window.location.href = payload.checkoutUrl
    } catch (error) {
      onUiState('error')
      onMessage(error instanceof Error ? error.message : 'Unable to start checkout.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section aria-labelledby="book-deposit-heading" className="book-step-panel">
      <h2 id="book-deposit-heading">Secure Your Booking</h2>
      <p className="book-pending-amount">
        {formatCurrencyFromCents(CERAMIC_BOOKING_DEPOSIT_CENTS)} Booking Deposit
      </p>
      <p>
        Your $100 booking deposit secures your appointment and will be deducted from your final
        invoice.
      </p>
      <p>
        The ceramic protection price shown is a From price. Selected add-ons may affect the final
        invoice after inspection. Only $100 is being charged now.
      </p>
      {stripeMode === 'test' ? (
        <p className="inspection-notice" role="status">Stripe test mode is active. No live charges will be made.</p>
      ) : null}
      {paid ? (
        <p className="selected-protection-note" role="status">
          Deposit Paid
        </p>
      ) : null}
      {uiState === 'verifying' ? (
        <p className="inspection-notice" role="status">Verifying your payment…</p>
      ) : null}
      {uiState === 'cancelled' ? (
        <p className="inspection-notice" role="status">
          Payment was not completed. Your booking details have been saved.
        </p>
      ) : null}
      {message ? <p className="inspection-notice" role="status">{message}</p> : null}
      {paid ? (
        <p>Continue to choose your preferred appointment window.</p>
      ) : (
        <Button disabled={busy || uiState === 'verifying' || !packageKey} onClick={() => void startCheckout()} type="button">
          {uiState === 'cancelled' ? 'Try Payment Again' : 'Pay $100 Deposit'}
        </Button>
      )}
    </section>
  )
}
